import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, map, Subject } from 'rxjs';
import { IEvent, IText } from 'fabric/fabric-impl';
import { fabric } from 'fabric';
import {
  SessionSharedDataService,
  SessionView,
} from '../../../services/session-shared-data.service';
import { DomListenerFactoryService } from '../../../services/dom-listener-factory.service';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { TelemetryService } from '../../../services/telemetry.service';
import { isBoardItem } from '../../common/y-session';
import { itemIsTextBoxOrItext, YCanvas } from '../wb-canvas/fabric-utils';
import { modifiedSetTimeout } from '../../../utilities/ZoneUtils';
import { BoardItem, FabricControls } from './board-items/board.item';

export interface Selection {
  object: fabric.Object;
  isMultiSelection: boolean;
  event: IEvent;
}

@Injectable({
  providedIn: 'root',
})
export class BoardItemsInteractionsManager {
  private NO_ITEM = 'NO_ITEM'; // placeholder used when no item is selected

  private canvas?: YCanvas;

  // Two observables accounting for the three states an object can be in when selected:
  // Selected Only: user can move, resize, rotate the object,
  // Selected and in Edit mode: user can only interact with the content of the object
  private _editModeEnabledItemId$ = new BehaviorSubject<string>(this.NO_ITEM);
  private _selectedBoardItem$ = new BehaviorSubject<BoardItem | undefined>(undefined);

  private domListener = this.domListenerFactoryService.createInstance();

  private boundFabricSelectionChange = this.fabricSelectionChanged.bind(this);
  private boundFabricSelectionCleared = this.deselectAll.bind(this);

  private boundCheckEditModeState = this.checkEditModeState.bind(this);

  // emits undefined when there is no selection active
  private _selection$ = new BehaviorSubject<Selection | undefined>(undefined);
  public selection$ = this._selection$.asObservable();

  private _mouseDownOnNonEventedItems$ = new Subject<string>();
  private _hasSelectionChanged = false;

  constructor(
    private sharedDataService: SessionSharedDataService,
    private domListenerFactoryService: DomListenerFactoryService,
    private flagsService: FlagsService,
    private telemetryService: TelemetryService,
  ) {}

  public init(canvas: YCanvas) {
    this.canvas = canvas;
    this.setUpFabricSelectionListeners();
    this.exitTextEditingModeWhenClickingOutsideFabric();
  }

  public destroyService(): void {
    this.canvas?.off('mouse:up:before', this.boundCheckEditModeState);
    this.canvas?.off('selection:updated', this.boundFabricSelectionChange);
    this.canvas?.off('selection:created', this.boundFabricSelectionChange);
    this.canvas?.off('selection:cleared', this.boundFabricSelectionCleared);
    this.deselectAll();
    this.domListener.clear();
  }

  public enableEditMode(boardItem: BoardItem) {
    // This assignment should be called before emitting to the subject
    // as it gets executed outside zone,
    // which might miss up the listeners that should be triggered after it
    this.sharedDataService.isDisableKeyEvents = true;
    this._editModeEnabledItemId$.next(boardItem.configurations.uid);

    // We want to make sure that items in edit mode are always selected,
    // to guarantee that edit mode => selected, selected =!=> edit mode
    // This a defense against the case where fabric canvas receives a click but the selection might not be enabled yet on it.
    const selectedObject = this.canvas?.getActiveObject();
    if (!selectedObject) {
      this.canvas?.setActiveObject(boardItem).requestRenderAll();
    }
  }

  public disableEditMode() {
    this._editModeEnabledItemId$.next(this.NO_ITEM);
    // key events means toolbar shortcuts
    this.sharedDataService.isDisableKeyEvents = false;
  }

  public itemSelected$(currentItemId: string) {
    return this._selectedBoardItem$.pipe(
      map((selectedBoardItem) => currentItemId === selectedBoardItem?.configurations?.uid),
      distinctUntilChanged(),
    );
  }

  public itemInEditMode$(currentItemId: string) {
    return this._editModeEnabledItemId$.pipe(
      map((itemId) => currentItemId === itemId),
      distinctUntilChanged(),
    );
  }

  public triggerDeselectAll(canvas?: YCanvas) {
    const fabricCanvas = canvas ?? this.canvas;
    if (!fabricCanvas) {
      return;
    }
    // Deselecting text boxes will cause unexpected behaviors
    // such as having the isEditing false while still having some properties set to values from the edit mode
    // such as having 'selectable' still set to false
    for (const object of fabricCanvas.getActiveObjects()) {
      if (itemIsTextBoxOrItext(object) && object.isEditing) {
        // skip firing object:modified event to not force exit editing on remote side
        (object as any).exitEditing(true);
      }
    }

    // SPAC-12568 preventing infinite loop when full-screening an app
    this._selection$.next(undefined);

    // This will trigger the selection:cleared fabric event which will trigger deselectAll method
    fabricCanvas?.discardActiveObject().requestRenderAll();
  }

  private exitTextEditingModeWhenClickingOutsideFabric() {
    // un focus fabric text when clicking outside fabric a fix for SPAC-969
    this.domListener.add(
      document,
      'click',
      (event) => {
        if (!this.canvas) {
          return;
        }
        if (event.target && event.target !== (this.canvas as any)?.upperCanvasEl) {
          const activeText = this.canvas.getActiveObject() as IText | null;
          if (activeText?.isEditing && activeText.exitEditing) {
            activeText.exitEditing();
          }
        }
      },
      true,
    );
  }

  nonEventedItemClickTimeout?: NodeJS.Timer;
  lastClickedNonEventedItem?: BoardItem;
  public onNonEventedItemClick(target: BoardItem) {
    const currentObjects = this.canvas?.getObjects() ?? [];
    const targetObjectIndex = currentObjects.indexOf(target);

    if (this.nonEventedItemClickTimeout) {
      clearTimeout(this.nonEventedItemClickTimeout);
      this.nonEventedItemClickTimeout = undefined;
    }

    if (
      this.lastClickedNonEventedItem === undefined || // not initialized before
      currentObjects.indexOf(this.lastClickedNonEventedItem) < targetObjectIndex // the current click is on an object that its stacking is above the current one
    ) {
      this.lastClickedNonEventedItem = target;
    }

    this.nonEventedItemClickTimeout = modifiedSetTimeout(() => {
      this.lastClickedNonEventedItem &&
        this._mouseDownOnNonEventedItems$.next(this.lastClickedNonEventedItem?.configurations.uid);
      this.lastClickedNonEventedItem = undefined;
    }, 50);
  }

  public getNonEventedItemClickObservable(uid: string) {
    return this._mouseDownOnNonEventedItems$.pipe(filter((currentUid) => currentUid === uid));
  }

  private setUpFabricSelectionListeners() {
    if (!this.canvas) {
      return;
    }

    // called as soon as the user clicks on an item, to check if edit mode should be enabled or not
    this.canvas.on('mouse:up:before', this.boundCheckEditModeState);
    // called when adding an item to the selection or when switching the selection from an object to another
    this.canvas.on('selection:updated', this.boundFabricSelectionChange);
    // called when selecting item/items and there wasn't any active selection before
    this.canvas.on('selection:created', this.boundFabricSelectionChange);
    // called when user clicks on an empty space in the canvas or when we call discardActiveObject
    this.canvas.on('selection:cleared', this.boundFabricSelectionCleared);
  }

  private fabricSelectionChanged(event: IEvent): void {
    if (!this.canvas) {
      return;
    }

    // if the deselected length is greater than 0, it means that the selection has changed not created
    if (event.deselected?.length) {
      this._hasSelectionChanged = true;
      this.deselectAll();
    } else {
      this._hasSelectionChanged = false;
    }

    const newlySelectedObjects = event.selected;
    if (!newlySelectedObjects?.length) {
      return;
    }

    this.reportSelectionStateToTelemetry(true);

    // The user created a multi-selection by adding an object to the previous selection, without discarding it
    if (this.selectedObject) {
      if (newlySelectedObjects.length === 1) {
        // If the user added only one object to the existing selection,
        // select the newly selected object alone if any of them is prevented from multi-selection
        const newlySelectedObject = newlySelectedObjects[0];

        // if either the previous object or the object added to the selection is prevented from multiSelection,
        // create a new selection that consists only of the last object the user clicked on
        if (
          this.isPreventedFromMultiSelection(newlySelectedObject) ||
          this.isPreventedFromMultiSelection(this.selectedObject)
        ) {
          this.replaceCurrentSelectionWithSelectionOf([newlySelectedObject]);
          return;
        }
      } else {
        // if the user added multiple objects to the existing selection
        // create a new selection consisting from the multi-selectable objects only.
        const selectableObjects = [...newlySelectedObjects, this.selectedObject].filter(
          (o) => !this.isPreventedFromMultiSelection(o),
        );
        // if either the previous object or the object added to the selection is prevented from multiSelection,
        // create a new selection that consists only of the last object the user clicked on
        if (selectableObjects.length !== newlySelectedObjects.length + 1) {
          this.replaceCurrentSelectionWithSelectionOf(selectableObjects);
          return;
        }
      }
    } else if (newlySelectedObjects.length > 1) {
      // The user created a normal multi-selection using mouse drag

      const multiSelectableObjects = newlySelectedObjects.filter(
        (object) => !this.isPreventedFromMultiSelection(object),
      );
      // selection contains an object that is not multi-selectable
      if (newlySelectedObjects.length !== multiSelectableObjects.length) {
        this.replaceCurrentSelectionWithSelectionOf(multiSelectableObjects);
        return;
      }
    }

    this.handleObjectsSelected(newlySelectedObjects, event);
  }

  private handleObjectsSelected(selectedObjects: fabric.Object[], event: IEvent) {
    if (selectedObjects.length === 0 || !this.canvas) {
      return;
    }

    const currentlyActiveObject = this.canvas.getActiveObject();

    if (
      isBoardItem(currentlyActiveObject) &&
      !this.selectedCanvasItem // To make sure that it is not a selection using CTRL + click, hence a multi-selection
    ) {
      this._selectedBoardItem$.next(currentlyActiveObject as BoardItem);
    }

    this.modifySelectionPropertiesBasedOnItsContent();

    if (currentlyActiveObject) {
      this._selection$.next({
        object: currentlyActiveObject,
        isMultiSelection: !!this.selectedObject || selectedObjects.length > 1,
        event,
      });
    }
  }

  private deselectAll(): void {
    if (this.sharedDataService.sessionView.getSessionView() === SessionView.FULLSCREEN_APP) {
      return;
    }

    this.reportSelectionStateToTelemetry(false);
    this.disableEditMode();
    this.canvas?.requestRenderAll();
    this._selectedBoardItem$.next(undefined);
    this._selection$.next(undefined);
  }

  private checkEditModeState(e: IEvent) {
    if (!e || !e.target || !e.absolutePointer || e.target.top === undefined) {
      return;
    }
    const boardItem = e.target;
    if (!isBoardItem(boardItem)) {
      return;
    }

    if (
      this.canvas?.selection && // Only when selection is enabled ( to handle the case of clicking when having the eraser tool active)
      boardItem.shouldEnterEditMode(
        e.absolutePointer.y,
        e.target.top,
        this._selection$.getValue()?.object === boardItem,
      )
    ) {
      boardItem.isEditing = true;
      this.enableEditMode(boardItem);
    } else {
      boardItem.isEditing = false;
      this.disableEditMode();
    }
  }

  private replaceCurrentSelectionWithSelectionOf(objects: fabric.Object[]) {
    this.triggerDeselectAll();
    if (objects.length && this.canvas) {
      this.canvas
        .setActiveObject(
          objects.length === 1
            ? objects[0]
            : new fabric.ActiveSelection(objects, {
                canvas: this.canvas,
              }),
        )
        .requestRenderAll();
    }
  }

  /**
   * Check if an object is locked using the same logic as board-items-interactions-manager
   */
  public isObjectLocked(object: fabric.Object): boolean {
    if (!this.flagsService.isFlagEnabled(FLAGS.SPACES_OBJECT_LOCK)) {
      return false;
    }

    return (object as any).locked === true || (isBoardItem(object) && object.configurations.locked);
  }

  private isPreventedFromMultiSelection(object?: fabric.Object) {
    if (!object) {
      return false;
    }
    return (
      // excluded from export object should follow its parent config so no need to process it
      object.excludeFromExport ||
      this.isObjectLocked(object) ||
      (isBoardItem(object) && !object.multiSelectable)
    );
  }

  public modifySelectionPropertiesBasedOnItsContent(): void {
    if (!this.canvas) {
      return;
    }
    const activeObjects = this.canvas.getActiveObjects();
    if (activeObjects.length <= 1) {
      return;
    }
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) {
      return;
    }

    // To prevent the objects from being deselected when clicking on the empty space in the selection
    activeObject.set('perPixelTargetFind', false);

    let selectedObjectsHaveControls = activeObject.hasControls;

    const fabricObjectsControlsNames = Object.values(FabricControls);
    const controlsVisibilities = {};
    for (const controlName of fabricObjectsControlsNames) {
      controlsVisibilities[controlName] = true;
    }

    const selectedObjectsLockedProperties = new Set<string>();
    for (const object of activeObjects) {
      // if any object doesn't have any control, the selection also shouldn't
      if (!object.hasControls) {
        selectedObjectsHaveControls = false;
      }

      // we show a control only if all the objects inside it have it visible
      for (const controlName of fabricObjectsControlsNames) {
        controlsVisibilities[controlName] =
          controlsVisibilities[controlName] && object.isControlVisible(controlName);
      }

      // if the object has a locked property: lockRotation, lockScalingX..., the group should also lock these properties
      Object.keys(object)
        .filter((key) => key.startsWith('lock'))
        .forEach((key) => {
          if (object[key] === true) {
            selectedObjectsLockedProperties.add(key);
          }
        });
    }

    Object.keys(activeObject)
      .filter((key) => key.startsWith('lock'))
      .forEach((key) => {
        activeObject[key] = selectedObjectsLockedProperties.has(key);
      });

    if (!selectedObjectsHaveControls) {
      activeObject.setControlsVisibility(
        fabricObjectsControlsNames.reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {}),
      );
    } else {
      activeObject.setControlsVisibility(controlsVisibilities);
    }
  }

  private reportSelectionStateToTelemetry(isSelectionActive: boolean): void {
    this.telemetryService.setSessionVars({ is_selecting: isSelectionActive });
  }

  public get selectedObject() {
    return this._selection$.getValue()?.object;
  }

  public get isSelectionActive() {
    return !!this._selection$.getValue();
  }

  public get selectedCanvasItem() {
    return this._selectedBoardItem$.getValue();
  }

  public get hasSelectionChanged() {
    return this._hasSelectionChanged;
  }
}
